import { useEffect, useState } from "react";
import { CONTENT, type P3Question } from "@/content";
import { BorderBeam } from "@/components/ui/border-beam";
import { apiFetch, ApiError } from "@/lib/api";
import { funnelState, type AnswersData } from "@/lib/funnelState";
import { fetchPublicQuestions, toP3Questions } from "@/lib/formQuestions";
import { StepBar } from "./StepBar";

type Props = {
  answers: AnswersData;
  setAnswers: (a: AnswersData) => void;
  onNext: () => void;
  onHome: () => void;
};

export function P3({ answers, setAnswers, onNext, onHome }: Props) {
  const p = CONTENT.p3;
  const [questions, setQuestions] = useState<P3Question[]>(() => [...CONTENT.p3.questions]);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicQuestions()
      .then((next) => {
        if (!cancelled && next?.length) setQuestions(toP3Questions(next));
      })
      .catch(() => {
        /* Mantem perguntas padrao se a API falhar. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (i: number, v: string | number) =>
    setAnswers({ ...answers, [i]: v });

  const answered = questions.filter(
    (_, i) => answers[i] !== undefined && answers[i] !== "",
  ).length;

  const submit = async () => {
    const leadId = funnelState.getLeadId();
    if (!leadId || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const payload = questions.map((q, i) => {
        const raw = answers[i];
        const text = q[0];
        const kind = q[2];
        if (kind === "scale") {
          return {
            idx: i,
            question_text: text,
            answer_scale: typeof raw === "number" ? raw : null,
          };
        }
        if (kind === "choice") {
          return {
            idx: i,
            question_text: text,
            answer_choice: typeof raw === "string" ? raw : null,
          };
        }
        return {
          idx: i,
          question_text: text,
          answer_text: typeof raw === "string" ? raw : null,
        };
      });
      await apiFetch(`/leads/${leadId}/answers`, {
        method: "POST",
        body: { answers: payload },
        auth: "lead",
      });
      onNext();
    } catch (err) {
      setServerError(
        err instanceof ApiError && err.status === 401
          ? "Sua sessão expirou. Volte ao início do agendamento."
          : "Não conseguimos salvar suas respostas. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fx page-enter">
      <StepBar step={1} onHome={onHome} />
      <main className="fx-main">
        <div className="fx-col">
          <span className="fx-kicker">{p.reinforce}</span>
          <h1 className="fx-h">{p.title}</h1>
          <p className="fx-open">{p.open}</p>
          <p className="fx-explain">
            {p.explain} {p.guidance}
          </p>

          <div className="fx-count-pill">
            {answered} de {questions.length} respondidas
          </div>

          <ol className="fx-questions">
            {questions.map((q, i) => {
              const [text, hint, kind] = q;
              const opts = kind === "choice" ? q[3] : undefined;
              const done = answers[i] !== undefined && answers[i] !== "";
              return (
                <li key={i} className={`fx-q ${done ? "done" : ""}`}>
                  <div className="fx-q-head">
                    <span className="fx-q-n">{String(i + 1).padStart(2, "0")}</span>
                    <div className="fx-q-text">
                      <h3 className="fx-q-t">{text}</h3>
                      {hint && <p className="fx-q-hint">{hint}</p>}
                    </div>
                  </div>
                  {kind === "text" && (
                    <textarea
                      className="fx-textarea"
                      rows={2}
                      placeholder="Sua resposta"
                      value={typeof answers[i] === "string" ? (answers[i] as string) : ""}
                      onChange={(e) => set(i, e.target.value)}
                    />
                  )}
                  {kind === "choice" && opts && (
                    <div className="fx-choices">
                      {opts.map((o) => (
                        <button
                          key={o}
                          type="button"
                          className={`fx-chip ${answers[i] === o ? "on" : ""}`}
                          onClick={() => set(i, o)}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  )}
                  {kind === "scale" && (
                    <div className="fx-scale">
                      {Array.from({ length: 10 }).map((_, n) => (
                        <button
                          key={n}
                          type="button"
                          className={`fx-scale-b ${answers[i] === n + 1 ? "on" : ""}`}
                          onClick={() => set(i, n + 1)}
                        >
                          {n + 1}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          {serverError && (
            <div className="adm-login-error" style={{ marginTop: 18 }}>{serverError}</div>
          )}

          <div className="fx-storyfoot">
            <h2 className="fx-thanks">{p.thanks}</h2>
            <p className="fx-transition">{p.transition}</p>
            <button
              className="btn btn-primary btn-lg btn-beam btn-beam-primary"
              onClick={submit}
              disabled={submitting}
            >
              <BorderBeam />
              {submitting ? "Salvando…" : p.button} <span className="arr">→</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
